import { mapGetters, mapActions, mapState } from 'vuex'

import { debounce } from '@/common/util/debounce'
import Events from '@/configuration/Events'
import { SmartFormClickMixin } from '@/host/components/mixins/SmartFormClick'

/**
 * Shared logic between Grid's SmartForm & SmartFormLegacy in regards to
 * CARDS methods selection with SinglePaymentButton + card form expanded.
 * Allow to auto-select CARDS when:
 *  - Any card form input get focused
 *  - Card header is clicked
 *  - Form is filled (e.g: with debug toolbar test card selection)
 *
 * @since KJS-3986
 */
export const CardSelectorMixin = {
  mixins: [SmartFormClickMixin],
  computed: {
    ...mapState(['activeForm']),
    ...mapState({
      selectedMethod: ({ smartForm }) => smartForm.selectedMethod
    }),
    ...mapGetters([
      'cardsFormExpanded',
      'hasCardInRoot',
      'hasCardFormExpandedInside',
      'isFormFilled',
      'isSinglePaymentButton',
      'isWalletSmartForm',
      'relatedForm',
      'isNestedMethod',
      'activeNestedMethod',
      'hasPreferredPaymentMethods',
      'isGridMode'
    ])
  },
  watch: {
    isFormFilled(val) {
      if (val) {
        this.onFormFilled()
      }
    }
  },
  created() {
    this.setDefaultActiveMethod()
    this.startExpandedFormSubscriptions()
    if (
      this.isSinglePaymentButton &&
      this.hasCardInRoot &&
      (this.hasCardFormExpandedInside || this.isGridMode) &&
      !this.isWalletSmartForm &&
      !this.hasPreferredPaymentMethods
    ) {
      this.selectMethod('CARDS')
    }
    this.onCardEventDebounced = debounce(params => {
      this.onCardEvent(params)
    }, 100)
  },
  methods: {
    ...mapActions(['selectMethod', 'setDefaultActiveMethod', 'update']),
    /**
     * For a SmartForm with expanded card form and single payment button,
     * the following event must cause the card to be seleted:
     * - a focus on input
     * - a click on the checkbox or select field
     *
     * If the form loses focus and is empty, CARDS should be unselected.
     *
     * @since KJS-2643
     * @see KJS-3986 | Move to mixin
     */
    startExpandedFormSubscriptions() {
      this.$busOn(Events.krypton.field.event, params => {
        const { type } = params
        switch (type) {
          case 'focusReport':
          case 'focus':
          case 'triggerFocusActions':
          case 'userClick':
            this.onCardEventDebounced(params)
        }
      })
    },
    async onCardEvent({ formId }) {
      const newActiveForm = this.relatedForm(formId)
      if (newActiveForm !== 'default' && newActiveForm === this.activeForm)
        return
      if (
        this.isSinglePaymentButton &&
        this.cardsFormExpanded &&
        !this.isNestedMethod(newActiveForm)
      ) {
        if (await this.interruptsExecution('CARDS', 'methodSelected')) return

        this.selectMethod('CARDS')
      }

      // A click on the expanded card form should change the active method if any nested method (ILLICADO, WERO...) is active
      if (newActiveForm === 'default' && !!this.activeNestedMethod)
        this.setDefaultActiveMethod()
    },
    /**
     * In case the form is filled without any of the event mentioned above
     * (e.g: by selecting a testcard on the toolbar) we should detect the form
     * as been (even partially) filled and set CARDS a the selected method.
     *
     * Do not allow the selection to be performed when it's not the default form
     * that has been filled. This causes a bug for ILLICADO form see KJS-5273
     *
     * @since KJS-2643
     * @see KJS-3986 | Move to mixin
     * @see KJS-5273 | Only apply if activeForm is DEFAULT
     */
    async onFormFilled() {
      if (
        this.isSinglePaymentButton &&
        this.cardsFormExpanded &&
        this.activeForm === 'DEFAULT'
      ) {
        if (await this.interruptsExecution('CARDS', 'methodSelected')) return
        this.selectMethod('CARDS')
      }
    }
  }
}
